import { useEffect, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function SettingsPage() {
  const [orderDelete, setOrderDelete] = useState(false);
  const [ann, setAnn] = useState({ announcement_enabled: false, announcement_text: '' });
  const [notify, setNotify] = useState([]);
  const [allNotify, setAllNotify] = useState([]);
  const [loading, setLoading] = useState(true);

  // OAuth
  const [oauth, setOauth] = useState(null);
  const [g, setG] = useState({ google_client_id: '', google_secret: '' });
  const [m, setM] = useState({ microsoft_client_id: '', microsoft_secret: '', microsoft_tenant: 'common' });

  const loadOauth = () => api.get('/api/settings/oauth').then(r => {
    setOauth(r.data);
    setG({ google_client_id: r.data.google.client_id || '', google_secret: '' });
    setM({ microsoft_client_id: r.data.microsoft.client_id || '', microsoft_secret: '', microsoft_tenant: r.data.microsoft.tenant || 'common' });
  }).catch(() => {});

  // Security (password policy + idle timeout)
  const [sec, setSec] = useState(null);
  const [savingSec, setSavingSec] = useState(false);
  const loadSecurity = () => api.get('/api/settings/security').then(r => setSec({
    min_length: r.data.password.min_length,
    require_classes: r.data.password.require_classes,
    block_common: !!r.data.password.block_common,
    session_idle_minutes: r.data.session_idle_minutes,
  })).catch(() => {});

  const saveSecurity = async () => {
    setSavingSec(true);
    try {
      await api.put('/api/settings/security', sec);
      toast.success('Security settings saved');
      loadSecurity();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setSavingSec(false); }
  };

  useEffect(() => {
    loadSecurity();
    api.get('/api/settings/general')
      .then(r => { setOrderDelete(!!r.data.order_delete_enabled); setAnn({ announcement_enabled: !!r.data.announcement_enabled, announcement_text: r.data.announcement_text || '' }); setNotify(r.data.notify_statuses || []); setAllNotify(r.data.all_notify_statuses || []); setLoading(false); })
      .catch(() => setLoading(false));
    loadOauth();
  }, []);

  const toggleNotify = async (status) => {
    const next = notify.includes(status) ? notify.filter(s => s !== status) : [...notify, status];
    setNotify(next);
    try { await api.put('/api/settings/general', { notify_statuses: next }); }
    catch (e) { toast.error('Save failed'); }
  };
  const NOTIFY_LABELS = { approved: 'Approved', in_production: 'In Production', shipped: 'Shipped', delivered: 'Delivered', closed: 'Closed', cancelled: 'Cancelled' };

  const saveAnnouncement = async () => {
    try { await api.put('/api/settings/general', ann); toast.success('Announcement saved'); }
    catch (e) { toast.error('Save failed'); }
  };

  const saveOauth = async (payload) => {
    try { await api.put('/api/settings/oauth', payload); toast.success('Login settings saved'); loadOauth(); }
    catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  };
  const clearProvider = async (provider) => {
    if (!window.confirm(`Remove the ${provider} login configuration?`)) return;
    try { await api.delete(`/api/settings/oauth/${provider}`); toast.success('Removed'); loadOauth(); }
    catch (e) { toast.error('Remove failed'); }
  };

  const toggle = async (v) => {
    setOrderDelete(v);
    try { await api.put('/api/settings/general', { order_delete_enabled: v }); toast.success('Settings saved'); }
    catch (e) { setOrderDelete(!v); toast.error(e.response?.data?.error || 'Save failed'); }
  };

  if (loading) return (<><div className="topbar"><h2>Settings</h2></div><div className="page"><div className="loading">Loading…</div></div></>);

  return (
    <>
      <div className="topbar"><h2>Settings</h2></div>
      <div className="page">
        {sec && (
          <div className="card" style={{ maxWidth: 620, marginBottom: 20 }}>
            <div className="card-header"><h3>Security</h3></div>
            <p className="form-hint" style={{ marginTop: 0 }}>
              Applies to every place a password is set — sign-in changes, admin resets and the
              welcome/reset links. Existing passwords are not affected until they are next changed.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Minimum length</label>
                <input className="form-input" type="number" min="8" max="128" value={sec.min_length}
                  onChange={e => setSec({ ...sec, min_length: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Character types required</label>
                <select className="form-select" value={sec.require_classes}
                  onChange={e => setSec({ ...sec, require_classes: parseInt(e.target.value, 10) })}>
                  <option value={1}>1 — any characters</option>
                  <option value={2}>2 of 4</option>
                  <option value={3}>3 of 4 (recommended)</option>
                  <option value={4}>All 4</option>
                </select>
                <p className="form-hint">Lowercase, uppercase, a number and a symbol.</p>
              </div>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14 }}>
                <input type="checkbox" checked={sec.block_common} style={{ marginTop: 3 }}
                  onChange={e => setSec({ ...sec, block_common: e.target.checked })} />
                <span>
                  <strong>Block common passwords</strong>
                  <div className="form-hint" style={{ marginTop: 4 }}>
                    Rejects the obvious ones, including the defaults this system shipped with.
                  </div>
                </span>
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Sign out after inactivity</label>
              <select className="form-select" style={{ maxWidth: 240 }} value={sec.session_idle_minutes}
                onChange={e => setSec({ ...sec, session_idle_minutes: parseInt(e.target.value, 10) })}>
                <option value={0}>Never</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={480}>8 hours</option>
              </select>
              <p className="form-hint">
                Users get a two-minute warning first, and typing counts as activity — so a long order
                form won't time out underneath someone.
              </p>
            </div>
            <button className="btn btn-primary btn-sm" onClick={saveSecurity} disabled={savingSec}>
              {savingSec ? 'Saving…' : 'Save security settings'}
            </button>
          </div>
        )}

        <div className="card" style={{ maxWidth: 620 }}>
          <div className="card-header"><h3>Orders</h3></div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14 }}>
            <input type="checkbox" checked={orderDelete} onChange={e => toggle(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              <strong>Allow permanently deleting orders</strong>
              <div className="form-hint" style={{ marginTop: 4 }}>
                When on, TTC admins get a <strong>Delete order</strong> button on the order screen (with a confirmation).
                Deleting an order is <strong>permanent</strong> and removes it and its files from history — it cannot be undone.
                Leave this off unless you specifically need it.
              </div>
            </span>
          </label>
        </div>

        <div className="card" style={{ maxWidth: 620, marginTop: 20 }}>
          <div className="card-header"><h3>Announcement banner</h3></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 10 }}>
            <input type="checkbox" checked={ann.announcement_enabled} onChange={e => setAnn(s => ({ ...s, announcement_enabled: e.target.checked }))} />
            Show a banner to all users
          </label>
          <textarea className="form-textarea" value={ann.announcement_text} onChange={e => setAnn(s => ({ ...s, announcement_text: e.target.value }))}
            placeholder="e.g. Holiday shipping cutoff is December 15." style={{ minHeight: 70 }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-primary" onClick={saveAnnouncement}>Save announcement</button>
          </div>
        </div>

        <div className="card" style={{ maxWidth: 620, marginTop: 20 }}>
          <div className="card-header"><h3>Order status notifications</h3></div>
          <p className="form-hint" style={{ marginTop: 0 }}>Which order status changes email the customer. (Requires email configured; unchecked statuses are silent.)</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {allNotify.map(st => (
              <label key={st} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, minWidth: 150 }}>
                <input type="checkbox" checked={notify.includes(st)} onChange={() => toggleNotify(st)} />
                {NOTIFY_LABELS[st] || st}
              </label>
            ))}
          </div>
        </div>

        <div className="card" style={{ maxWidth: 620, marginTop: 20 }}>
          <div className="card-header"><h3>Login with Microsoft</h3></div>
          <p className="form-hint" style={{ marginTop: 0 }}>
            Status: {oauth?.microsoft.configured ? <strong style={{ color: 'var(--brand-green)' }}>configured</strong> : <strong style={{ color: 'var(--gray-500)' }}>not set up</strong>}.
            Register one multi-tenant app in Microsoft Entra (customers set up nothing). Redirect URI:
            {' '}<code style={{ fontSize: 12 }}>{oauth?.redirect_uris.microsoft}</code>
          </p>
          <div className="form-group"><label className="form-label">Application (client) ID</label>
            <input className="form-input" value={m.microsoft_client_id} onChange={e => setM(s => ({ ...s, microsoft_client_id: e.target.value }))} /></div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 2 }}><label className="form-label">Client secret</label>
              <input className="form-input" type="password" value={m.microsoft_secret} onChange={e => setM(s => ({ ...s, microsoft_secret: e.target.value }))}
                placeholder={oauth?.microsoft.has_secret ? '•••••••• (saved — leave blank to keep)' : 'Enter the secret value'} /></div>
            <div className="form-group" style={{ flex: 1 }}><label className="form-label">Tenant</label>
              <input className="form-input" value={m.microsoft_tenant} onChange={e => setM(s => ({ ...s, microsoft_tenant: e.target.value }))} placeholder="common" /></div>
          </div>
          <p className="form-hint">Tenant <code>common</code> = any company's Office 365 + personal accounts. Use <code>organizations</code> for work/school only.</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {oauth?.microsoft.configured && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--brand-red)' }} onClick={() => clearProvider('microsoft')}>Remove</button>}
            <button className="btn btn-primary" onClick={() => saveOauth(m)}>Save Microsoft</button>
          </div>
        </div>

        <div className="card" style={{ maxWidth: 620, marginTop: 20 }}>
          <div className="card-header"><h3>Login with Google</h3></div>
          <p className="form-hint" style={{ marginTop: 0 }}>
            Status: {oauth?.google.configured ? <strong style={{ color: 'var(--brand-green)' }}>configured</strong> : <strong style={{ color: 'var(--gray-500)' }}>not set up</strong>}.
            Register one OAuth client in Google Cloud (customers set up nothing). Redirect URI:
            {' '}<code style={{ fontSize: 12 }}>{oauth?.redirect_uris.google}</code>
          </p>
          <div className="form-group"><label className="form-label">Client ID</label>
            <input className="form-input" value={g.google_client_id} onChange={e => setG(s => ({ ...s, google_client_id: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Client secret</label>
            <input className="form-input" type="password" value={g.google_secret} onChange={e => setG(s => ({ ...s, google_secret: e.target.value }))}
              placeholder={oauth?.google.has_secret ? '•••••••• (saved — leave blank to keep)' : 'Enter the client secret'} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {oauth?.google.configured && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--brand-red)' }} onClick={() => clearProvider('google')}>Remove</button>}
            <button className="btn btn-primary" onClick={() => saveOauth(g)}>Save Google</button>
          </div>
        </div>

        <div className="card" style={{ maxWidth: 620, marginTop: 20, background: 'var(--gray-50)' }}>
          <p className="form-hint" style={{ margin: 0 }}>
            <strong>How sign-in works:</strong> a user must already exist in the OMS with their work email. When they sign in with Microsoft or Google, they're matched by email — random accounts can't get in, and their company doesn't set up anything. Secrets are stored encrypted; changes apply immediately without a restart.
          </p>
        </div>
      </div>
    </>
  );
}
